import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import LoadingPlaceholder from "../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import { useAppStore } from "../../../../hooks/useStores";
import { ICatalogReport } from "../../../../models/attribution-reports";
import styles from "./AttributionWidgetStats.module.scss";

const MAX_ITEMS = 8;
const OTHER_COLOR = "#B3B3B9";
const COLORS = ["#5EB8FC", "#5E88FC", "#74DBEF", "#74EFE1", "#CFFFFF", "#B05EFC", "#7B5EFC", "#7D95B6"];

type Props = {
	startDate?: string;
	endDate?: string;
};

const AttributionWidgetStats = ({ startDate = "", endDate = "" }: Props) => {
	const { t } = useTranslation("dashboard", { keyPrefix: "attribution" });
	const { authStore, attributionReportsStore } = useAppStore();
	const [attributionData, setAttributionData] = useState<ICatalogReport[]>([]);

	useEffect(() => {
		const fetchStats = async () => {
			const today = new Date();
			const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
			attributionReportsStore.selectedDateRange = [firstDayOfMonth, today];

			const minCacheDate = new Date(today);
			minCacheDate.setDate(today.getDate() - 1);

			const fullMerged: ICatalogReport[] = await attributionReportsStore.loadCatalogReportData(minCacheDate);
			setAttributionData(fullMerged);
		};

		fetchStats();
	}, []);

	const sorted = [...attributionData].sort((a, b) => b.imagesAttributed - a.imagesAttributed);
	const top = sorted.slice(0, MAX_ITEMS).map((item, index) => ({
		...item,
		color: COLORS[index] || "#ccc",
	}));
	const otherTotal = sorted.slice(MAX_ITEMS).reduce((sum, item) => sum + item.imagesAttributed, 0);

	const fullData = [
		...top,
		...(otherTotal > 0 ? [{ partner: t("other"), imagesAttributed: otherTotal, color: OTHER_COLOR }] : []),
	];

	const total = fullData.reduce((sum, item) => sum + item.imagesAttributed, 0);
	const hasData = total > 0;

	const renderTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const { partner, imagesAttributed } = payload[0].payload;
			const percent = ((imagesAttributed / total) * 100).toFixed(2);
			return (
				<Box className={styles.tooltip}>
					{percent}%<br />
					{partner}
				</Box>
			);
		}
		return null;
	};

	return (
		<LoadingPlaceholder
			isLoading={authStore.isLoadingOrgSubscriptions || attributionReportsStore.isCatalogReportDataLoading}
			className={styles.container}
		>
			<Box className={styles.chartWrapper}>
				<Box className={styles.header}>
					<Typography className={styles.subtitle}>{t("subtitle")}</Typography>
					<Trans>
						<Typography className={styles.description}>{t("description")}</Typography>
					</Trans>
					<ul className={styles.legend}>
						{fullData.map(({ partner, color }) => (
							<li key={partner} className={styles.legendItem}>
								<span className={styles.colorSquare} style={{ backgroundColor: color }} />
								<span className={styles.legendLabel}>{partner}</span>
							</li>
						))}
					</ul>
				</Box>
				<Box className={styles.chartContainer}>
					{hasData ? (
						<ResponsiveContainer minWidth={156} width={"100%"} aspect={1}>
							<PieChart>
								<Pie
									data={fullData}
									dataKey="imagesAttributed"
									nameKey="partner"
									cx="50%"
									cy="50%"
									innerRadius="40%"
									outerRadius="70%"
									startAngle={-270}
									endAngle={90}
								>
									{fullData.map((entry, index) => (
										<Cell key={`cell-${index}`} fill={entry.color} />
									))}
								</Pie>
								<Tooltip content={renderTooltip} />
							</PieChart>
						</ResponsiveContainer>
					) : (
						<Box className={styles.noDataMessage}>
							<Typography variant="body2" color="text.secondary">
								{t("noData", {
									startDate: startDate,
									endDate: endDate,
								})}
							</Typography>
						</Box>
					)}
				</Box>
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(AttributionWidgetStats);
